/**
 * BattleContext - Phase 5
 *
 * バトル画面で使用する全状態を Context パターンで統合管理し、
 * props drilling を排除する。
 *
 * 使用方法:
 * 1. magic-spirit.tsx で BattleProvider でバトルUIをラップ
 * 2. 子コンポーネントで useBattle() で状態を取得
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { BattleProviderProps, BattleContextValue } from '../../types/battle';

// Context 作成
const BattleContext = createContext<BattleContextValue | null>(null);

/**
 * BattleProvider
 * バトル画面で使用する全状態を提供
 */
export const BattleProvider: React.FC<BattleProviderProps> = ({
  children,
  // ========================================
  // 既存の Hook インスタンス（magic-spirit.tsx から渡す）
  // ========================================
  selection,      // useBattleSelection() の戻り値
  setsuna,        // useSetsunaMagic() の戻り値
  gigen,          // useGigen() の戻り値
  miraiYochi,     // useMiraiYochi() の戻り値
  sourei,         // useSourei() の戻り値
  hengenmi,       // useHengenmi() の戻り値
  deepSea,        // useDeepSea() の戻り値

  // ========================================
  // バトル状態（engineState から）
  // ========================================
  currentPlayer,
  phase,
  turn,
  isFirstTurn,
  winner,
  p1Life, p2Life,
  p1Field, p2Field,
  p1Hand, p2Hand,
  p1Deck, p2Deck,
  p1Graveyard, p2Graveyard,
  p1ActiveSP, p2ActiveSP,
  p1RestedSP, p2RestedSP,
  p1FieldCard, p2FieldCard,
  p1PhaseCard, p2PhaseCard,
  p1StatusEffects, p2StatusEffects,

  // ========================================
  // Setters（dispatch 経由）
  // ========================================
  setP1Life, setP2Life,
  setP1Field, setP2Field,
  setP1Hand, setP2Hand,
  setP1Deck, setP2Deck,
  setP1Graveyard, setP2Graveyard,
  setP1ActiveSP, setP2ActiveSP,
  setP1RestedSP, setP2RestedSP,
  setP1FieldCard, setP2FieldCard,
  setP1PhaseCard, setP2PhaseCard,

  // ========================================
  // バトルアクション関数
  // ========================================
  summonCard,
  executeAttack,
  executeSkill,
  processPhase,
  nextPhase,
  chargeCard,
  chargeSP,
  attack,
  addLog,

  // ========================================
  // その他
  // ========================================
  isMobile,
  summoningSickness,
  logs,
}) => {
  // Context value をメモ化してパフォーマンス最適化
  const value = useMemo<BattleContextValue>(() => ({
    // Selection state
    ...selection,

    // Keyword abilities
    setsuna,
    gigen,
    miraiYochi,
    sourei,
    hengenmi,
    deepSea,

    // Battle state
    currentPlayer,
    phase,
    turn,
    isFirstTurn,
    winner,
    p1Life, p2Life,
    p1Field, p2Field,
    p1Hand, p2Hand,
    p1Deck, p2Deck,
    p1Graveyard, p2Graveyard,
    p1ActiveSP, p2ActiveSP,
    p1RestedSP, p2RestedSP,
    p1FieldCard, p2FieldCard,
    p1PhaseCard, p2PhaseCard,
    p1StatusEffects, p2StatusEffects,

    // Setters
    setP1Life, setP2Life,
    setP1Field, setP2Field,
    setP1Hand, setP2Hand,
    setP1Deck, setP2Deck,
    setP1Graveyard, setP2Graveyard,
    setP1ActiveSP, setP2ActiveSP,
    setP1RestedSP, setP2RestedSP,
    setP1FieldCard, setP2FieldCard,
    setP1PhaseCard, setP2PhaseCard,

    // Actions
    summonCard,
    executeAttack,
    executeSkill,
    processPhase,
    nextPhase,
    chargeCard,
    chargeSP,
    attack,
    addLog,

    // Other
    isMobile,
    summoningSickness,
    logs,
  }), [
    selection,
    setsuna, gigen, miraiYochi, sourei, hengenmi, deepSea,
    currentPlayer, phase, turn, isFirstTurn, winner,
    p1Life, p2Life, p1Field, p2Field, p1Hand, p2Hand,
    p1Deck, p2Deck, p1Graveyard, p2Graveyard,
    p1ActiveSP, p2ActiveSP, p1RestedSP, p2RestedSP,
    p1FieldCard, p2FieldCard, p1PhaseCard, p2PhaseCard,
    p1StatusEffects, p2StatusEffects,
    setP1Life, setP2Life, setP1Field, setP2Field,
    setP1Hand, setP2Hand, setP1Deck, setP2Deck,
    setP1Graveyard, setP2Graveyard,
    setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
    setP1FieldCard, setP2FieldCard, setP1PhaseCard, setP2PhaseCard,
    summonCard, executeAttack, executeSkill, processPhase, nextPhase,
    chargeCard, chargeSP, attack, addLog,
    isMobile, summoningSickness, logs,
  ]);

  return (
    <BattleContext.Provider value={value}>
      {children}
    </BattleContext.Provider>
  );
};

/**
 * useBattle - バトル状態を取得するカスタムフック
 *
 * @returns バトル状態と関数
 * @throws BattleProvider 外で使用した場合
 *
 * @example
 * const { currentPlayer, phase, attack } = useBattle();
 */
export const useBattle = (): BattleContextValue => {
  const context = useContext(BattleContext);
  if (!context) {
    throw new Error('useBattle must be used within BattleProvider');
  }
  return context;
};

export default BattleContext;
